/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the React application.
 * Handles auth state changes, user data, and provides loading states.
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from 'firebase/auth';
import {
  signInWithGoogle,
  signOutUser,
  onAuthStateChange,
  formatAuthUser,
  type AuthResult,
  type AuthUser
} from '@/firebase';
import { FirebaseUser, AuthState, FirebaseError } from '@/types/firebase';

// Context interface
interface AuthContextType extends AuthState {
  // Auth methods
  signIn: () => Promise<AuthResult>;
  signOut: () => Promise<AuthResult>;
  
  // User data
  firebaseUser: FirebaseUser | null;
  authUser: AuthUser | null;
  
  // Utilities
  refreshUser: () => Promise<void>;
  isInitialized: boolean;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: true,
    error: null
  });
  
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * Handle Firebase auth state changes
   */
  useEffect(() => {
    console.log('🔐 Setting up auth state listener...');
    
    const unsubscribe = onAuthStateChange(async (firebaseUser: User | null) => {
      console.log('🔐 Auth state changed:', firebaseUser ? 'User signed in' : 'User signed out');
      
      try {
        if (firebaseUser) {
          // User is signed in
          const formattedUser = formatAuthUser(firebaseUser);
          setAuthUser(formattedUser);
          
          // PRODUCTION MODE: Use real Firestore operations
          console.log('🔥 PRODUCTION MODE: Creating real Firestore user');
          
          try {
            // Import user service
            const { getOrCreateUser } = await import('@/services');
            
            // Create or get user from Firestore
            const userResult = await getOrCreateUser(firebaseUser.uid, {
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              emailVerified: firebaseUser.emailVerified
            });
            
            if (userResult.success && userResult.data) {
              setAuthState({
                isAuthenticated: true,
                user: userResult.data,
                loading: false,
                error: null
              });
              console.log('✅ Real Firestore user setup complete');
            } else {
              throw new Error(userResult.error?.message || 'Failed to create user');
            }
          } catch (error) {
            console.error('🚫 Firestore user creation failed:', error);
            
            // Fallback to temporary user
            console.log('🛠️ Falling back to temporary user');
            const tempFirebaseUser: FirebaseUser = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              emailVerified: firebaseUser.emailVerified,
              createdAt: new Date() as any,
              updatedAt: new Date() as any,
              profile: {
                firstName: firebaseUser.displayName?.split(' ')[0] || '',
                lastName: firebaseUser.displayName?.split(' ')[1] || '',
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                language: navigator.language || 'en'
              },
              preferences: {
                enableSounds: false,
                enableHapticFeedback: false,
                showFingerHints: true,
                preferredDifficulty: 'beginner'
              },
              overallStats: {
                totalSessions: 0,
                totalTrainingTime: 0,
                bestAccuracy: 0,
                bestSpeed: 0,
                bestWPM: 0,
                currentStreak: 0,
                longestStreak: 0
              },
              lastSession: null
            };
            
            setAuthState({
              isAuthenticated: true,
              user: tempFirebaseUser,
              loading: false,
              error: null
            });
          }
        } else {
          // User is signed out
          setAuthUser(null);
          setAuthState({
            isAuthenticated: false,
            user: null,
            loading: false,
            error: null
          });
        }
      } catch (error) {
        console.error('🚫 Error handling auth state change:', error);
        const firebaseError: FirebaseError = {
          code: 'auth/state-change-error',
          message: error instanceof Error ? error.message : 'Unknown auth error',
          details: error
        };
        
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: firebaseError
        });
      } finally {
        if (!isInitialized) {
          setIsInitialized(true);
          console.log('🔐 Auth initialized');
        }
      }
    });

    // Cleanup subscription
    return () => {
      console.log('🔐 Cleaning up auth state listener');
      unsubscribe();
    };
  }, [isInitialized]);

  /**
   * Sign in with Google
   */
  const signIn = async (): Promise<AuthResult> => {
    console.log('🔐 Initiating sign in...');
    
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await signInWithGoogle();
      
      if (!result.success) {
        const firebaseError: FirebaseError = {
          code: 'auth/sign-in-failed',
          message: result.error || 'Sign in failed',
        };
        
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: firebaseError
        }));
      }
      
      return result;
    } catch (error) {
      console.error('🚫 Sign in error:', error);
      
      const firebaseError: FirebaseError = {
        code: 'auth/sign-in-error',
        message: error instanceof Error ? error.message : 'Unknown sign in error',
        details: error
      };
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: firebaseError
      }));
      
      return { success: false, error: firebaseError.message };
    }
  };

  /**
   * Sign out user
   */
  const signOut = async (): Promise<AuthResult> => {
    console.log('🔐 Initiating sign out...');
    
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await signOutUser();
      
      if (!result.success) {
        const firebaseError: FirebaseError = {
          code: 'auth/sign-out-failed',
          message: result.error || 'Sign out failed',
        };
        
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: firebaseError
        }));
      }
      
      return result;
    } catch (error) {
      console.error('🚫 Sign out error:', error);
      
      const firebaseError: FirebaseError = {
        code: 'auth/sign-out-error',
        message: error instanceof Error ? error.message : 'Unknown sign out error',
        details: error
      };
      
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: firebaseError
      }));
      
      return { success: false, error: firebaseError.message };
    }
  };

  /**
   * Refresh user data
   */
  const refreshUser = async (): Promise<void> => {
    console.log('🔄 Refreshing user data...');
    
    try {
      // TODO: Implement user data refresh from Firestore in Sprint 2
      console.log('✅ User data refreshed (placeholder)');
    } catch (error) {
      console.error('🚫 Failed to refresh user data:', error);
    }
  };



  // Context value
  const value: AuthContextType = {
    // Auth state
    ...authState,
    
    // User data
    firebaseUser: authState.user,
    authUser,
    
    // Auth methods
    signIn,
    signOut,
    
    // Utilities
    refreshUser,
    isInitialized
  };

  // Show loading screen until auth is initialized
  if (!isInitialized) {
    return (
      <div className="auth-loading">
        <div className="loading-spinner"></div>
        <p>Initializing authentication...</p>
        
        <style>{`
          .auth-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            color: #ffffff;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }
          
          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 4px solid rgba(59, 130, 246, 0.3);
            border-top-color: #3b82f6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
          }
          
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          p {
            margin: 0;
            color: #9ca3af;
            font-size: 0.875rem;
          }
        `}</style>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth guard component for protected routes
interface AuthGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  fallback 
}) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="auth-guard-loading">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p>Checking authentication...</p>
        </div>
        
        <style>{`
          .auth-guard-loading {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            color: #ffffff;
          }
          
          .loading-content {
            text-align: center;
          }
          
          .loading-spinner {
            width: 32px;
            height: 32px;
            border: 3px solid rgba(59, 130, 246, 0.3);
            border-top-color: #3b82f6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 12px;
          }
          
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          p {
            margin: 0;
            color: #9ca3af;
            font-size: 0.875rem;
          }
        `}</style>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
};

// Development helpers
if (import.meta.env.DEV) {
  console.log('🔐 Auth context initialized in development mode');
}
